import { PlaylistAdd } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, FormControl, InputLabel, MenuItem, Select, SelectChangeEvent,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { Media } from '../../Model/MediaModel'
import { Playlist } from '../../Model/PlaylistModel'
import { toPlaylistDTO, useUpdatePlaylistMutation } from '../../service/PlaylistService'
import { useAppSelector } from '../../store/hooks'
import config from '../../Util/config'
import { mediaToPlaylistItem } from '../../Util/playlistItem'

const MAX_ITEMS = config.FEATURE.MAX_PLAYLIST_ITEMS

type Props = {
  open: boolean
  onClose: () => void
  media: Media | undefined
}

const ModalAddToPlaylist = ({ open, onClose, media }: Props) => {
  const navigate = useNavigate()
  const playlists = useAppSelector(s => s.playlist)
  const [selectedId, setSelectedId] = useState<number | ''>('')
  const [updatePlaylist, { isLoading }] = useUpdatePlaylistMutation()

  function handleClose() {
    setSelectedId('')
    onClose()
  }

  async function handleSave(andGo: boolean) {
    if (!media || selectedId === '') return

    const playlist = playlists.find(p => p.id === selectedId)
    if (!playlist) return

    if (playlist.items.length >= MAX_ITEMS) {
      toast.warn(`Playlist já tem o máximo de ${MAX_ITEMS} itens`)
      return
    }

    const updated: Playlist = {
      ...playlist,
      items: [
        ...playlist.items,
        mediaToPlaylistItem(media, playlist.items.length + 1),
      ],
    }

    try {
      await updatePlaylist({ playlist: toPlaylistDTO(updated) }).unwrap()
      toast.success(`Adicionado à "${playlist.name}"`)
      handleClose()
      if (andGo) navigate(`/playlists/edit/${playlist.id}`)
    } catch {
      toast.error('Falha ao adicionar à playlist')
    }
  }

  const selected = playlists.find(p => p.id === selectedId)
  const atLimit = selected ? selected.items.length >= MAX_ITEMS : false

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ pb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <PlaylistAdd sx={{ color: 'primary.main' }} />
          <Typography fontWeight={600}>Adicionar à playlist</Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1.5 }}>
        <Typography variant="body2" color="text.secondary" noWrap sx={{ mb: 2 }}>
          {media?.desc}
        </Typography>

        <FormControl fullWidth size="small">
          <InputLabel>Escolha uma playlist</InputLabel>
          <Select
            label="Escolha uma playlist"
            value={selectedId}
            onChange={(e: SelectChangeEvent<number | ''>) => setSelectedId(e.target.value as number)}
          >
            {playlists.map(p => (
              <MenuItem key={p.id} value={p.id} disabled={p.items.length >= MAX_ITEMS}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                  <Typography variant="body2" noWrap>{p.name}</Typography>
                  <Typography variant="caption" color={p.items.length >= MAX_ITEMS ? 'error' : 'text.disabled'} sx={{ flexShrink: 0 }}>
                    {p.items.length}/{MAX_ITEMS}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {atLimit && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            Playlist cheia — remova um item antes de adicionar.
          </Typography>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2.5, py: 1.5, gap: 1 }}>
        <Button variant="outlined" size="small" onClick={handleClose}
          sx={{ color: 'text.secondary', borderColor: 'divider' }}>
          Cancelar
        </Button>
        <Box sx={{ flex: 1 }} />
        <LoadingButton variant="outlined" size="small" loading={isLoading}
          disabled={selectedId === '' || atLimit}
          onClick={() => handleSave(false)}>
          Salvar
        </LoadingButton>
        <LoadingButton variant="contained" size="small" loading={isLoading}
          disabled={selectedId === '' || atLimit}
          onClick={() => handleSave(true)}>
          Salvar e ir
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}

export default ModalAddToPlaylist
